// Staking Flexible with Yield Boost Types

export interface FlexibleStakingPool {
  id: string;
  name: string;
  token: string;
  baseApy: number;
  boostedApy: number; // APY with all boosts active
  tvl: string;
  userStaked: string;
  pendingRewards: string;
  isActive: boolean;

  // Boost configuration
  boosts: YieldBoost[];
  userBoosts: UserBoostStatus[];
}

export interface YieldBoost {
  id: string;
  name: string;
  description: string;
  type: BoostType;
  bonusApy: number; // Additional APY percentage
  requirement: BoostRequirement;
  icon: string;
  isStackable: boolean;
  maxDuration?: number; // In days, undefined = permanent while active
}

export type BoostType =
  | 'lock_duration'      // Longer lock = higher APY
  | 'nft_holder'         // Hold specific NFT
  | 'token_amount'       // Stake more tokens
  | 'loyalty'            // Time-based loyalty bonus
  | 'referral'           // Referred users staking
  | 'compound'           // Auto-compound enabled
  | 'early_adopter'      // Early user bonus
  | 'governance'         // Active governance participation
  | 'multi_chain';       // Stake on multiple chains

export interface BoostRequirement {
  type: 'min_stake' | 'min_duration' | 'nft_ownership' | 'referral_count' | 'time_staked' | 'compound_enabled' | 'governance_votes' | 'chains_count';
  value: number | string;
  currentValue?: number | string;
  isMet: boolean;
}

export interface UserBoostStatus {
  boostId: string;
  isActive: boolean;
  activatedAt?: number;
  expiresAt?: number;
  currentBonusApy: number;
  progress?: number; // 0-100 for progressive boosts
}

export interface StakingCalculation {
  baseAmount: string;
  baseApy: number;
  activeBoosts: {
    boost: YieldBoost;
    contribution: number;
  }[];
  totalBoostedApy: number;
  estimatedDailyReward: string;
  estimatedMonthlyReward: string;
  estimatedYearlyReward: string;
}

export interface BoostActivationParams {
  poolId: string;
  boostId: string;
  lockDuration?: number; // For lock_duration boosts
}

export interface FlexibleStakingStats {
  totalStaked: string;
  totalStakedUSD: string;
  averageApy: number;
  highestApy: number;
  totalBoostsActive: number;
  totalRewardsDistributed: string;
}

// Predefined boost templates
export const YIELD_BOOSTS: YieldBoost[] = [
  {
    id: 'lock-7-days',
    name: 'Compromiso Semanal',
    description: 'Bloquea tus tokens por 7 dias',
    type: 'lock_duration',
    bonusApy: 2.0,
    requirement: { type: 'min_duration', value: 7, isMet: false },
    icon: '7d',
    isStackable: false,
  },
  {
    id: 'lock-30-days',
    name: 'Compromiso Mensual',
    description: 'Bloquea tus tokens por 30 dias',
    type: 'lock_duration',
    bonusApy: 5.0,
    requirement: { type: 'min_duration', value: 30, isMet: false },
    icon: '30d',
    isStackable: false,
  },
  {
    id: 'lock-90-days',
    name: 'Compromiso Trimestral',
    description: 'Bloquea tus tokens por 90 dias',
    type: 'lock_duration',
    bonusApy: 10.0,
    requirement: { type: 'min_duration', value: 90, isMet: false },
    icon: '90d',
    isStackable: false,
  },
  {
    id: 'whale-tier',
    name: 'Whale Tier',
    description: 'Stakea mas de 50,000 NDH',
    type: 'token_amount',
    bonusApy: 3.0,
    requirement: { type: 'min_stake', value: 50000, isMet: false },
    icon: 'whale',
    isStackable: true,
  },
  {
    id: 'diamond-hands',
    name: 'Diamond Hands',
    description: 'Mantener stake por mas de 180 dias',
    type: 'loyalty',
    bonusApy: 4.0,
    requirement: { type: 'time_staked', value: 180, isMet: false },
    icon: 'diamond',
    isStackable: true,
  },
  {
    id: 'auto-compound',
    name: 'Compounder',
    description: 'Activa auto-compound de recompensas',
    type: 'compound',
    bonusApy: 1.5,
    requirement: { type: 'compound_enabled', value: 1, isMet: false },
    icon: 'compound',
    isStackable: true,
  },
  {
    id: 'referral-master',
    name: 'Referral Master',
    description: 'Refiere 5+ usuarios que stakeen',
    type: 'referral',
    bonusApy: 2.5,
    requirement: { type: 'referral_count', value: 5, isMet: false },
    icon: 'referral',
    isStackable: true,
  },
  {
    id: 'governance-active',
    name: 'Gobernanza Activa',
    description: 'Vota en al menos 3 propuestas',
    type: 'governance',
    bonusApy: 1.0,
    requirement: { type: 'governance_votes', value: 3, isMet: false },
    icon: 'governance',
    isStackable: true,
  },
  {
    id: 'early-bird',
    name: 'Early Bird',
    description: 'Staker en los primeros 30 dias del pool',
    type: 'early_adopter',
    bonusApy: 2.0,
    requirement: { type: 'time_staked', value: 0, isMet: false },
    icon: 'early',
    isStackable: true,
    maxDuration: 365,
  },
  {
    id: 'multi-chain-staker',
    name: 'Multi-Chain Staker',
    description: 'Stakea en 3+ cadenas diferentes',
    type: 'multi_chain',
    bonusApy: 3.5,
    requirement: { type: 'chains_count', value: 3, isMet: false },
    icon: 'multichain',
    isStackable: true,
  },
];

export const BOOST_TYPE_COLORS: Record<BoostType, string> = {
  lock_duration: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
  nft_holder: 'bg-purple-500/20 text-purple-500 border-purple-500/30',
  token_amount: 'bg-green-500/20 text-green-500 border-green-500/30',
  loyalty: 'bg-amber-500/20 text-amber-500 border-amber-500/30',
  referral: 'bg-pink-500/20 text-pink-500 border-pink-500/30',
  compound: 'bg-cyan-500/20 text-cyan-500 border-cyan-500/30',
  early_adopter: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
  governance: 'bg-indigo-500/20 text-indigo-500 border-indigo-500/30',
  multi_chain: 'bg-violet-500/20 text-violet-500 border-violet-500/30',
};
